import { Plus, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState } from "../components/ui";
import { QueryError, QueryLoading } from "../components/QueryState";
import { useTeamData } from "../hooks/usePlatformData";
import { useOrganization } from "../auth/OrganizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import type { TeamListRow } from "../repositories/platform";
import { env } from "../lib/env";
import { subscribeDemoReset } from "../lib/demo-session";
import {
  canOnboardMember,
  canCreatePersistentSeller,
  loadSellerSetup,
  runSellerCommand,
} from "../lib/seller-contract-commands";

type AccessFilter = "TODOS" | "ATIVO" | "PENDENTE" | "SUSPENSO";

function accessState(status: string) {
  if (status === "ATIVO") return { label: "Ativa", tone: "success" as const };
  if (status === "INATIVO") return { label: "Suspensa", tone: "danger" as const };
  if (status === "ENCERRADO") return { label: "Cadastro encerrado", tone: "neutral" as const };
  return { label: "Primeiro acesso pendente", tone: "warning" as const };
}

type SellerForm = { name: string; email: string; team: string; supervisor: string };
type SellerErrors = Partial<Record<keyof SellerForm, string>>;
type LocalMember = TeamListRow & { supervisorId?: string };

const emptySeller: SellerForm = { name: "", email: "", team: "", supervisor: "" };

export function toLocalMember(member: TeamListRow, supervisorId?: string): LocalMember {
  return { ...member, supervisorId };
}

export function validateSeller(form: SellerForm): SellerErrors {
  const errors: SellerErrors = {};
  if (form.name.trim().length < 3) errors.name = "Informe o nome completo.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "Informe um e-mail válido.";
  }
  if (!form.team) errors.team = "Selecione uma equipe.";
  if (!form.supervisor) errors.supervisor = "Selecione um supervisor.";
  return errors;
}

export function TeamPage() {
  const query = useTeamData();
  const { activeMembership, activeOrganizationId } = useOrganization();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("TODAS");
  const [supervisor, setSupervisor] = useState("TODOS");
  const [access, setAccess] = useState<AccessFilter>("TODOS");
  const [showNewSeller, setShowNewSeller] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<string>();
  const [localMembers, setLocalMembers] = useState<LocalMember[] | null>(null);
  const [sellerForm, setSellerForm] = useState<SellerForm>(emptySeller);
  const [sellerErrors, setSellerErrors] = useState<SellerErrors>({});
  const [formNotice, setFormNotice] = useState("");
  const [sellerSetup, setSellerSetup] = useState<Awaited<
    ReturnType<typeof loadSellerSetup>
  > | null>(null);
  const [setupError, setSetupError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [commandBusy, setCommandBusy] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [membershipIdToOnboard, setMembershipIdToOnboard] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [teamId, setTeamId] = useState("");
  const [suspensionReason, setSuspensionReason] = useState("");
  useEffect(() => {
    if (env.useMocks || !activeOrganizationId) return;
    let disposed = false;
    setSetupLoading(true);
    setSetupError("");
    void loadSellerSetup(activeOrganizationId, activeMembership?.id ?? null)
      .then((result) => {
        if (!disposed) setSellerSetup(result);
      })
      .catch((error: unknown) => {
        if (!disposed)
          setSetupError(
            error instanceof Error ? error.message : "Não foi possível carregar opções da equipe.",
          );
      })
      .finally(() => {
        if (!disposed) setSetupLoading(false);
      });
    return () => {
      disposed = true;
    };
  }, [activeMembership?.id, activeOrganizationId]);
  useEffect(() => {
    if (env.useMocks && query.data && localMembers === null) {
      const firstSupervisor = query.data.find((member) =>
        member.role.toLocaleLowerCase("pt-BR").includes("supervisor"),
      );
      setLocalMembers(
        query.data.map((member) =>
          toLocalMember(
            member,
            member.role.toLocaleLowerCase("pt-BR").includes("supervisor")
              ? undefined
              : firstSupervisor?.id,
          ),
        ),
      );
    }
  }, [localMembers, query.data]);
  useEffect(
    () =>
      subscribeDemoReset(() => {
        setLocalMembers(null);
        setSelectedMemberId(undefined);
        setShowNewSeller(false);
        setSellerForm(emptySeller);
        setSellerErrors({});
        setFormNotice("");
        setEmployeeId("");
        setReferralCode("");
        setTeamId("");
        setSuspensionReason("");
        setSearch("");
        setTeam("TODAS");
        setSupervisor("TODOS");
        setAccess("TODOS");
      }),
    [],
  );
  const allRows: LocalMember[] =
    localMembers ??
    (query.data ?? []).map((member) => {
      const actualTeamId = sellerSetup?.sellerTeamIds[member.id];
      const actualTeam = sellerSetup?.teams.find((item) => item.id === actualTeamId);
      return toLocalMember(
        env.useMocks ? member : { ...member, team: actualTeam?.name ?? "Sem equipe" },
        env.useMocks ? undefined : (actualTeam?.supervisorEmployeeId ?? undefined),
      );
    });
  const teams = useMemo(() => [...new Set(allRows.map((member) => member.team))], [allRows]);
  const supervisors = useMemo(() => {
    if (env.useMocks)
      return allRows.filter((member) =>
        member.role.toLocaleLowerCase("pt-BR").includes("supervisor"),
      );
    const assignedSupervisorIds = new Set(
      (sellerSetup?.teams ?? [])
        .map((item) => item.supervisorEmployeeId)
        .filter((id): id is string => Boolean(id)),
    );
    return (sellerSetup?.employees ?? [])
      .filter((employee) => assignedSupervisorIds.has(employee.id))
      .map((employee) => ({
        id: employee.id,
        name: employee.name,
        role: "Supervisor",
        team: "",
        referral: "",
        sales: 0,
        status: "ATIVO",
      }));
  }, [allRows, sellerSetup]);
  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    return allRows.filter((member) => {
      const searchable = [member.name, member.role, member.team, member.referral]
        .join(" ")
        .toLocaleLowerCase("pt-BR");
      const normalizedAccess =
        member.status === "ATIVO"
          ? "ATIVO"
          : member.status === "INATIVO"
            ? "SUSPENSO"
            : member.status === "ENCERRADO"
              ? "ENCERRADO"
              : "PENDENTE";
      return (
        (!term || searchable.includes(term)) &&
        (team === "TODAS" || member.team === team) &&
        (supervisor === "TODOS" || member.supervisorId === supervisor) &&
        (access === "TODOS" || normalizedAccess === access)
      );
    });
  }, [access, allRows, search, supervisor, team]);

  if (query.isLoading) return <QueryLoading label="Carregando equipe comercial…" />;
  if (query.error) return <QueryError error={query.error} />;
  const active = allRows.filter((member) => member.status === "ATIVO").length;
  const supervisorCount = supervisors.length;
  const pending = allRows.filter(
    (member) => member.status !== "ATIVO" && member.status !== "INATIVO",
  ).length;

  function updateSellerField<K extends keyof SellerForm>(field: K, value: SellerForm[K]) {
    setSellerForm((current) => ({ ...current, [field]: value }));
    setSellerErrors((current) => ({ ...current, [field]: undefined }));
    setFormNotice("");
  }

  async function createSeller() {
    if (!env.useMocks) {
      if (!activeOrganizationId) {
        setFormNotice("Selecione uma organização ativa antes de cadastrar.");
        return;
      }
      if (!employeeId || !referralCode.trim()) {
        setFormNotice(
          "Selecione um colaborador ativo sem vendedor e informe o código de indicação.",
        );
        return;
      }
      setCommandBusy(true);
      setFormNotice("");
      let sellerWasCreated = false;
      try {
        const result = (await runSellerCommand("command_create_seller", {
          organizationId: activeOrganizationId,
          employeeId,
          referralCode: referralCode.trim(),
        })) as { sellerId?: string };
        sellerWasCreated = Boolean(result.sellerId);
        if (teamId && result.sellerId) {
          await runSellerCommand("command_assign_seller_to_team", {
            organizationId: activeOrganizationId,
            sellerId: result.sellerId,
            teamId,
          });
        }
        await queryClient.invalidateQueries({ queryKey: ["scope"] });
        setSellerSetup(await loadSellerSetup(activeOrganizationId, activeMembership?.id ?? null));
        setShowNewSeller(false);
        setEmployeeId("");
        setReferralCode("");
        setTeamId("");
        setFormNotice("Vendedor cadastrado no servidor. Nenhuma credencial foi criada ou exibida.");
      } catch (error) {
        const detail = error instanceof Error ? error.message : "Falha ao cadastrar vendedor.";
        if (sellerWasCreated && activeOrganizationId) {
          await queryClient.invalidateQueries({ queryKey: ["scope"] });
          try {
            setSellerSetup(
              await loadSellerSetup(activeOrganizationId, activeMembership?.id ?? null),
            );
          } catch {
            setSetupError("O vendedor foi criado, mas a lista precisa ser recarregada.");
          }
          setShowNewSeller(false);
          setEmployeeId("");
          setReferralCode("");
          setTeamId("");
          setFormNotice(
            `Vendedor cadastrado, mas não foi possível concluir a associação da equipe: ${detail}`,
          );
        } else {
          setFormNotice(detail);
        }
      } finally {
        setCommandBusy(false);
      }
      return;
    }
    const errors = validateSeller(sellerForm);
    if (Object.keys(errors).length > 0) {
      setSellerErrors(errors);
      setFormNotice("Revise os campos destacados antes de salvar.");
      return;
    }
    const newSeller: LocalMember = {
      id: `fake-seller-${Date.now()}`,
      name: sellerForm.name.trim(),
      role: "Vendedor",
      team: sellerForm.team,
      referral: `DEMO${String(allRows.length + 1).padStart(2, "0")}`,
      sales: 0,
      status: "PENDENTE",
      supervisorId: sellerForm.supervisor,
    };
    setLocalMembers([...allRows, newSeller]);
    setSelectedMemberId(newSeller.id);
    setSellerForm(emptySeller);
    setSellerErrors({});
    setFormNotice(
      "Vendedor criado somente nesta demonstração local. Nenhuma credencial temporária foi exibida ou armazenada.",
    );
  }

  async function createEmployeeForMember() {
    if (!activeOrganizationId || !membershipIdToOnboard) return;
    setCommandBusy(true);
    setFormNotice("");
    try {
      const result = (await runSellerCommand("command_create_employee", {
        organizationId: activeOrganizationId,
        membershipId: membershipIdToOnboard,
      })) as { employeeId?: string };
      const nextSetup = await loadSellerSetup(activeOrganizationId, activeMembership?.id ?? null);
      setSellerSetup(nextSetup);
      setMembershipIdToOnboard("");
      if (result.employeeId) setEmployeeId(result.employeeId);
      setFormNotice(
        "Cadastro operacional criado. Selecione o colaborador e conclua o cadastro de vendedor.",
      );
    } catch (error) {
      setFormNotice(
        error instanceof Error ? error.message : "Não foi possível cadastrar o colaborador.",
      );
    } finally {
      setCommandBusy(false);
    }
  }

  async function toggleSuspension(memberId: string) {
    if (!env.useMocks) {
      if (!activeOrganizationId) return;
      const member = allRows.find((row) => row.id === memberId);
      if (!member) return;
      if (member.status === "ENCERRADO") {
        setFormNotice("Este cadastro está encerrado e não pode ser reativado por esta tela.");
        return;
      }
      const suspending = member.status !== "INATIVO";
      if (suspending && suspensionReason.trim().length < 3) {
        setFormNotice("Informe um motivo operacional para suspender o acesso.");
        return;
      }
      setCommandBusy(true);
      setFormNotice("");
      try {
        await runSellerCommand("command_set_seller_status", {
          organizationId: activeOrganizationId,
          sellerId: memberId,
          status: suspending ? "SUSPENDED" : "ACTIVE",
          ...(suspending ? { reason: suspensionReason.trim() } : {}),
        });
        await queryClient.invalidateQueries({ queryKey: ["scope"] });
        setSuspensionReason("");
        setFormNotice("Status do vendedor atualizado no servidor.");
      } catch (error) {
        setFormNotice(error instanceof Error ? error.message : "Falha ao atualizar o status.");
      } finally {
        setCommandBusy(false);
      }
      return;
    }
    setLocalMembers(
      allRows.map((member) =>
        member.id === memberId
          ? { ...member, status: member.status === "INATIVO" ? "ATIVO" : "INATIVO" }
          : member,
      ),
    );
  }

  async function assignSupervisor(memberId: string, selectedTeamId: string) {
    if (!env.useMocks) {
      if (!activeOrganizationId || !sellerSetup) return;
      const selectedTeam = sellerSetup.teams.find((item) => item.id === selectedTeamId);
      if (!selectedTeam) return;
      setCommandBusy(true);
      setFormNotice("");
      try {
        await runSellerCommand("command_assign_seller_to_team", {
          organizationId: activeOrganizationId,
          sellerId: memberId,
          teamId: selectedTeam.id,
        });
        await queryClient.invalidateQueries({ queryKey: ["scope"] });
        setSellerSetup(await loadSellerSetup(activeOrganizationId, activeMembership?.id ?? null));
        setFormNotice("Equipe do vendedor atualizada no servidor.");
      } catch (error) {
        setFormNotice(error instanceof Error ? error.message : "Falha ao atualizar a equipe.");
      } finally {
        setCommandBusy(false);
      }
      return;
    }
    setLocalMembers(
      allRows.map((member) =>
        member.id === memberId ? { ...member, supervisorId: selectedTeamId } : member,
      ),
    );
  }

  return (
    <div className="page-stack">
      <section className="page-heading-row">
        <div>
          <p className="eyebrow">OPERAÇÃO COMERCIAL</p>
          <h2>Equipe comercial</h2>
          <p className="muted">
            Acompanhe pessoas, equipes e carteiras com o acesso de cada vendedor.
          </p>
        </div>
        <Button onClick={() => setShowNewSeller((current) => !current)}>
          <Plus size={16} /> Novo vendedor
        </Button>
      </section>
      <div className="inline-notice" role="note">
        <UserRound size={18} />
        <div>
          <strong>{env.useMocks ? "Demonstração segura" : "Operação conectada pendente"}</strong>
          <span>
            {env.useMocks
              ? "Cadastros, atribuições e suspensões ficam somente nesta sessão e usam dados fictícios."
              : "Ações persistentes usam comandos autenticados do servidor e exigem IDs válidos da organização ativa."}
          </span>
        </div>
      </div>
      {!env.useMocks && !showNewSeller && formNotice && (
        <p className="form-status" role="status">
          {formNotice}
        </p>
      )}
      <section className="metric-grid metric-grid-four" aria-label="Resumo da equipe comercial">
        <article className="metric-card">
          <span>Equipe cadastrada</span>
          <strong>{allRows.length}</strong>
          <small>Vendedores e supervisão</small>
        </article>
        <article className="metric-card">
          <span>Acessos ativos</span>
          <strong>{active}</strong>
          <small>Prontos para operar</small>
        </article>
        <article className="metric-card">
          <span>Supervisores</span>
          <strong>{supervisorCount}</strong>
          <small>Gestão de equipe</small>
        </article>
        <article className="metric-card">
          <span>Primeiro acesso</span>
          <strong>{pending}</strong>
          <small>Aguardando ativação</small>
        </article>
      </section>
      {showNewSeller && (
        <Card
          title="Novo vendedor"
          description={
            env.useMocks
              ? "No modo demo, o cadastro fica somente nesta sessão do navegador."
              : "Use um colaborador existente ou, com permissão administrativa, habilite um membro ativo já cadastrado. Não cria usuários nem convites."
          }
        >
          {env.useMocks ? (
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Nome completo</span>
                <input
                  value={sellerForm.name}
                  onChange={(event) => updateSellerField("name", event.target.value)}
                  aria-invalid={Boolean(sellerErrors.name)}
                  placeholder="Nome do vendedor"
                />
                {sellerErrors.name && <span className="field-error">{sellerErrors.name}</span>}
              </label>
              <label className="field">
                <span className="field-label">E-mail de trabalho</span>
                <input
                  value={sellerForm.email}
                  onChange={(event) => updateSellerField("email", event.target.value)}
                  aria-invalid={Boolean(sellerErrors.email)}
                  type="email"
                  placeholder="vendedor@empresa.com"
                />
                {sellerErrors.email && <span className="field-error">{sellerErrors.email}</span>}
              </label>
              <label className="field">
                <span className="field-label">Equipe</span>
                <select
                  value={sellerForm.team}
                  onChange={(event) => updateSellerField("team", event.target.value)}
                  aria-invalid={Boolean(sellerErrors.team)}
                >
                  <option value="" disabled>
                    Selecione uma equipe
                  </option>
                  {teams.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
                {sellerErrors.team && <span className="field-error">{sellerErrors.team}</span>}
              </label>
              <label className="field">
                <span className="field-label">Supervisor</span>
                <select
                  value={sellerForm.supervisor}
                  onChange={(event) => updateSellerField("supervisor", event.target.value)}
                  aria-invalid={Boolean(sellerErrors.supervisor)}
                >
                  <option value="" disabled>
                    Selecione um supervisor
                  </option>
                  {supervisors.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {sellerErrors.supervisor && (
                  <span className="field-error">{sellerErrors.supervisor}</span>
                )}
              </label>
            </div>
          ) : (
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Colaborador ativo sem cadastro de vendedor</span>
                <select
                  value={employeeId}
                  onChange={(event) => setEmployeeId(event.target.value)}
                  disabled={setupLoading || !sellerSetup?.eligibleEmployees.length}
                >
                  <option value="">
                    {setupLoading ? "Carregando colaboradores…" : "Selecione um colaborador"}
                  </option>
                  {(sellerSetup?.eligibleEmployees ?? []).map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                      {employee.employeeNumber ? ` · ${employee.employeeNumber}` : ""}
                    </option>
                  ))}
                </select>
                {!setupLoading && sellerSetup?.eligibleEmployees.length === 0 && (
                  <span className="field-hint">
                    {sellerSetup.employees.length === 0
                      ? sellerSetup.canManageEmployees
                        ? sellerSetup.onboardingMemberships.length > 0
                          ? "Ainda não há cadastro operacional. Um administrador pode habilitar abaixo um membro ativo já existente."
                          : "Não há membro ativo disponível para cadastro operacional neste tenant."
                        : "Ainda não há cadastro operacional de colaborador. Um administrador com permissão admin.full pode habilitar um membro ativo."
                      : "Todos os colaboradores elegíveis já têm cadastro de vendedor nesta organização."}
                  </span>
                )}
              </label>
              {sellerSetup?.eligibleEmployees.length === 0 &&
                sellerSetup.canManageEmployees &&
                sellerSetup.onboardingMemberships.length > 0 && (
                  <div className="field form-grid__full">
                    <label className="field">
                      <span className="field-label">Membro ativo existente</span>
                      <select
                        value={membershipIdToOnboard}
                        onChange={(event) => setMembershipIdToOnboard(event.target.value)}
                        disabled={setupLoading || commandBusy}
                      >
                        <option value="">Selecione uma pessoa da organização</option>
                        {sellerSetup.onboardingMemberships.map((membership) => (
                          <option key={membership.id} value={membership.id}>
                            {membership.name}
                          </option>
                        ))}
                      </select>
                      <span className="field-hint">
                        Cria somente o cadastro operacional ligado à membership atual. Não cria
                        usuário, convite ou papel de acesso.
                      </span>
                    </label>
                    <Button
                      variant="secondary"
                      onClick={() => void createEmployeeForMember()}
                      disabled={
                        !canOnboardMember(
                          membershipIdToOnboard,
                          commandBusy || setupLoading,
                          sellerSetup.canManageEmployees,
                          sellerSetup.onboardingMemberships.map((membership) => membership.id),
                        )
                      }
                    >
                      {commandBusy ? "Salvando…" : "Criar cadastro operacional"}
                    </Button>
                  </div>
                )}
              <label className="field">
                <span className="field-label">Código de indicação</span>
                <input
                  value={referralCode}
                  onChange={(event) => setReferralCode(event.target.value)}
                  placeholder="Código definido pela operação"
                  maxLength={80}
                />
              </label>
              <label className="field">
                <span className="field-label">Equipe (opcional)</span>
                <select
                  value={teamId}
                  onChange={(event) => setTeamId(event.target.value)}
                  disabled={!sellerSetup?.teams.length}
                >
                  <option value="">Sem equipe por enquanto</option>
                  {(sellerSetup?.teams ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {!sellerSetup?.teams.length && (
                  <span className="field-hint">
                    Nenhuma equipe persistente disponível; o vendedor poderá ficar sem equipe.
                  </span>
                )}
              </label>
              {setupError && (
                <p className="field-error" role="alert">
                  Não foi possível carregar as opções da equipe: {setupError}
                </p>
              )}
            </div>
          )}
          {formNotice && (
            <p className="form-status" role="status">
              {formNotice}
            </p>
          )}
          <div className="card-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowNewSeller(false);
                setSellerErrors({});
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void createSeller()}
              disabled={
                commandBusy ||
                (!env.useMocks &&
                  !canCreatePersistentSeller({
                    employeeId,
                    referralCode,
                    loading: setupLoading,
                    eligibleEmployeeIds:
                      sellerSetup?.eligibleEmployees.map((employee) => employee.id) ?? [],
                  }))
              }
            >
              {commandBusy ? "Salvando…" : "Salvar vendedor"}
            </Button>
          </div>
        </Card>
      )}
      <Card
        title="Vendedores e supervisão"
        description="Use os filtros para localizar uma pessoa ou acompanhar uma carteira específica."
      >
        <div className="toolbar" aria-label="Filtros da equipe">
          <label className="search-box">
            <span className="sr-only">Buscar vendedor</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome ou código"
            />
          </label>
          <select
            value={team}
            onChange={(event) => setTeam(event.target.value)}
            aria-label="Filtrar por equipe"
          >
            <option value="TODAS">Todas as equipes</option>
            {teams.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
          <select
            value={supervisor}
            onChange={(event) => setSupervisor(event.target.value)}
            aria-label="Filtrar por supervisor"
          >
            <option value="TODOS">Todos os supervisores</option>
            {supervisors.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <select
            value={access}
            onChange={(event) => setAccess(event.target.value as AccessFilter)}
            aria-label="Filtrar por acesso"
          >
            <option value="TODOS">Todos os acessos</option>
            <option value="ATIVO">Acessos ativos</option>
            <option value="PENDENTE">Primeiro acesso pendente</option>
            <option value="SUSPENSO">Acessos suspensos</option>
            <option value="ENCERRADO">Cadastros encerrados</option>
          </select>
        </div>
        {rows.length === 0 ? (
          <EmptyState
            title="Nenhum vendedor encontrado"
            description={
              env.useMocks
                ? "Ajuste os filtros ou cadastre o primeiro vendedor da equipe."
                : sellerSetup?.employees.length === 0
                  ? "Não há employee com membership ativo neste tenant. O onboarding do funcionário precisa ser provisionado antes do cadastro do vendedor."
                  : "Ajuste os filtros ou selecione um colaborador ativo elegível para cadastrar o primeiro vendedor persistente."
            }
          />
        ) : (
          <DataTable
            rows={rows}
            getKey={(member) => member.id}
            columns={[
              {
                key: "seller",
                header: "Pessoa",
                render: (member) => (
                  <div>
                    <strong>{member.name}</strong>
                    <div className="muted">{member.role}</div>
                  </div>
                ),
              },
              { key: "team", header: "Equipe", render: (member) => member.team },
              {
                key: "supervisor",
                header: "Supervisor",
                render: (member) =>
                  env.useMocks
                    ? member.role.toLocaleLowerCase("pt-BR").includes("supervisor")
                      ? "—"
                      : (supervisors.find((item) => item.id === member.supervisorId)?.name ??
                        "Não atribuído")
                    : (() => {
                        const teamRow = sellerSetup?.teams.find(
                          (item) => item.name === member.team,
                        );
                        return (
                          sellerSetup?.employees.find(
                            (employee) => employee.id === teamRow?.supervisorEmployeeId,
                          )?.name ?? "Não atribuído"
                        );
                      })(),
              },
              {
                key: "portfolio",
                header: "Carteira",
                render: (member) => `${member.sales} vendas no mês`,
              },
              {
                key: "access",
                header: "Acesso",
                render: (member) => {
                  const state = accessState(member.status);
                  return <Badge tone={state.tone}>{state.label}</Badge>;
                },
              },
              {
                key: "action",
                header: "Ação",
                render: (member) => (
                  <Button variant="ghost" onClick={() => setSelectedMemberId(member.id)}>
                    <UserRound size={15} /> Ver perfil
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>
      {selectedMemberId &&
        (() => {
          const member = allRows.find((row) => row.id === selectedMemberId);
          if (!member) return null;
          const state = accessState(member.status);
          return (
            <Card
              title={`Perfil · ${member.name}`}
              description="Resumo operacional da pessoa e da carteira."
            >
              <div className="three-feature-grid">
                <div className="feature-item">
                  <strong>Equipe</strong>
                  <p>{member.team}</p>
                </div>
                <div className="feature-item">
                  <strong>Perfil de acesso</strong>
                  <p>
                    {member.role} · <Badge tone={state.tone}>{state.label}</Badge>
                  </p>
                </div>
                <div className="feature-item">
                  <strong>Carteira</strong>
                  <p>
                    {member.sales} vendas no mês · código {member.referral}
                  </p>
                </div>
              </div>
              {member.role.toLocaleLowerCase("pt-BR").includes("supervisor") ? null : (
                <label className="field">
                  <span className="field-label">
                    {env.useMocks ? "Supervisor responsável" : "Equipe atribuída"}
                  </span>
                  <select
                    value={
                      env.useMocks
                        ? (member.supervisorId ?? "")
                        : (sellerSetup?.teams.find((item) => item.name === member.team)?.id ?? "")
                    }
                    onChange={(event) => void assignSupervisor(member.id, event.target.value)}
                    disabled={commandBusy || (!env.useMocks && !sellerSetup?.teams.length)}
                  >
                    <option value="" disabled={!env.useMocks}>
                      {" "}
                      {env.useMocks ? "Não atribuído" : "Selecione uma equipe"}
                    </option>
                    {env.useMocks
                      ? supervisors.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))
                      : (sellerSetup?.teams ?? []).map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                  </select>
                  <span className="field-hint">
                    {env.useMocks
                      ? "A atribuição de demonstração altera apenas a carteira desta sessão."
                      : "A equipe selecionada será gravada por comando autenticado no servidor."}
                  </span>
                </label>
              )}
              {!env.useMocks && member.status !== "INATIVO" && member.status !== "ENCERRADO" && (
                <label className="field">
                  <span className="field-label">Motivo da suspensão</span>
                  <input
                    value={suspensionReason}
                    onChange={(event) => setSuspensionReason(event.target.value)}
                    placeholder="Motivo operacional (obrigatório)"
                  />
                </label>
              )}
              <div className="card-actions">
                <Button
                  variant="secondary"
                  onClick={() => void toggleSuspension(member.id)}
                  disabled={commandBusy}
                >
                  {member.status === "INATIVO"
                    ? "Reativar acesso"
                    : member.status === "ENCERRADO"
                      ? "Cadastro encerrado"
                      : "Suspender acesso"}
                </Button>
                <Button variant="secondary" onClick={() => setSelectedMemberId(undefined)}>
                  Fechar perfil
                </Button>
              </div>
            </Card>
          );
        })()}
    </div>
  );
}
